import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { LayoutDashboard, Wrench, Settings, Sun, Moon, LogOut, HelpCircle, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../hooks/useAuth.jsx';
import { useTheme } from '../hooks/useTheme.jsx';
import clsx from 'clsx';
import HelpModal from './HelpModal';
import FeedbackModal from './FeedbackModal';


const navItems = [
  { path: '/', label: 'Overview', icon: <LayoutDashboard size={20} /> },
  { path: '/tools', label: 'Products', icon: <Wrench size={20} /> },
  { path: '/settings', label: 'Settings', icon: <Settings size={20} /> },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [showHelp, setShowHelp] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  return (
    <div className="h-screen flex overflow-hidden bg-surface-50 dark:bg-surface-900 transition-colors duration-300">
      {/* Sidebar */}
      <aside className={clsx(
        "bg-white dark:bg-surface-800 border-r border-surface-200 dark:border-surface-700 flex flex-col transition-all duration-300",
        sidebarCollapsed ? "w-20" : "w-64"
      )}>
        {/* Logo */}
        <div className="p-6 border-b border-surface-200 dark:border-surface-700">
          <div className="flex items-center justify-center">
            {sidebarCollapsed ? (
              <img
                src="/logo-icon.svg"
                alt="EdFrame"
                className="w-10 h-10"
              />
            ) : (
              <img
                src="/logo-text-v2.svg"
                alt="EdFrame"
                className="h-12 w-auto"
              />
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4">
          <ul className="space-y-1">
            {navItems.filter(item => {
              if (item.path === '/settings') return user?.role === 'admin';
              if (item.path === '/tools') return user?.role === 'admin' || user?.role === 'manager';
              return true;
            }).map((item) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  title={sidebarCollapsed ? item.label : undefined}
                  className={({ isActive }) =>
                    clsx(
                      'flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-200',
                      sidebarCollapsed && 'justify-center',
                      isActive
                        ? 'bg-brand-50 text-brand-600 dark:bg-brand-500/10 dark:text-brand-400 font-medium'
                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-surface-700/50'
                    )
                  }
                >
                  <span>{item.icon}</span>
                  {!sidebarCollapsed && <span className="font-medium">{item.label}</span>}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-surface-200 dark:border-surface-700 space-y-3">
          {!sidebarCollapsed && (
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 bg-surface-100 dark:bg-surface-700 rounded-full flex items-center justify-center text-sm font-medium text-slate-700 dark:text-slate-200 shrink-0">
                  {user?.displayName?.[0] || user?.email?.[0] || '?'}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{user?.displayName || 'User'}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{user?.email}</p>
                </div>
              </div>
            </div>
          )}

          <div className={clsx(
            "flex items-center gap-2",
            sidebarCollapsed ? "flex-col" : "justify-center"
          )}>
            <button
              onClick={toggleTheme}
              className="relative group p-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 bg-surface-100 dark:bg-surface-700/50 hover:bg-surface-200 dark:hover:bg-surface-700 rounded-lg transition-all duration-200"
              aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            >
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
              <span className="absolute left-full ml-2 px-2 py-1 bg-slate-900 dark:bg-slate-700 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50">
                {theme === 'dark' ? 'Light' : 'Dark'} mode
              </span>
            </button>
            <button
              onClick={() => setShowHelp(true)}
              className="relative group p-2 text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 bg-surface-100 dark:bg-surface-700/50 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all duration-200"
              aria-label="Help"
            >
              <HelpCircle size={18} />
              <span className="absolute left-full ml-2 px-2 py-1 bg-slate-900 dark:bg-slate-700 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50">
                Help
              </span>
            </button>
            <button
              onClick={() => setShowFeedback(true)}
              className="relative group p-2 text-slate-600 dark:text-slate-400 hover:text-amber-600 dark:hover:text-amber-400 bg-surface-100 dark:bg-surface-700/50 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-all duration-200"
              aria-label="Updates & Feedback"
            >
              <AlertCircle size={18} />
              <span className="absolute left-full ml-2 px-2 py-1 bg-slate-900 dark:bg-slate-700 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50">
                Feedback
              </span>
            </button>
            <button
              onClick={logout}
              className="relative group p-2 text-slate-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 bg-surface-100 dark:bg-surface-700/50 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all duration-200"
              aria-label="Logout"
            >
              <LogOut size={18} />
              <span className="absolute left-full ml-2 px-2 py-1 bg-slate-900 dark:bg-slate-700 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50">
                Logout
              </span>
            </button>
          </div>

          {/* Collapse button */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="relative group w-full p-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 bg-surface-100 dark:bg-surface-700/50 hover:bg-surface-200 dark:hover:bg-surface-700 rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
            aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {sidebarCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            {!sidebarCollapsed && <span className="text-sm font-medium">Collapse</span>}
            {sidebarCollapsed && (
              <span className="absolute left-full ml-2 px-2 py-1 bg-slate-900 dark:bg-slate-700 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50">
                Expand sidebar
              </span>
            )}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto p-8">
          {children}
        </div>
      </main>

      {/* Modal overlays */}
      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} />
      <FeedbackModal isOpen={showFeedback} onClose={() => setShowFeedback(false)} />
    </div>
  );
}

